// LockPlan: a small IR that encodes *where the locks go*.
//
// The decision of how to run each `txn` — optimistically (validate + retry) or
// pessimistically (lock a statically-known footprint) — and how to represent
// memory for the whole program (versioned MVCC vs. plain mutable-under-lock) is
// computed here, once, independent of any backend. A backend (see gogen.ts for
// the Go one) consumes a LockPlan and lowers it; a different target (LLVM, C,
// WASM…) could consume the same plan without redoing the analysis.

import * as ast from "../frontend/ast.js";
import { Elaboration } from "./elaboration.js";
import { Type } from "../frontend/types.js";

// ---------------------------------------------------------------------------
// The IR
// ---------------------------------------------------------------------------

export type TxnStrategy =
  | { kind: "occ" } // optimistic: snapshot, buffer writes, validate read set, retry on conflict
  | { kind: "lock"; footprint: string[] }; // pessimistic 2PL: lock these root variables, run once

/**
 * How the whole program represents heap objects:
 *   "mvcc"     — versioned records + a global clock; reads are lock-free; OCC works.
 *   "in-place" — plain mutable records under a per-object lock, no versioning. Only
 *                legal when every txn is pessimistic (no OCC) and nothing reads an
 *                object's fields outside a locked transaction — then a txn is just
 *                "lock, mutate in place, unlock", identical to hand-written locking.
 */
export type MemoryMode = "mvcc" | "in-place";

export interface LockPlan {
  mode: MemoryMode;
  /** Per-`txn` strategy, keyed by AST node identity. */
  strategies: Map<ast.TxnStmt, TxnStrategy>;
  txnCount: number;
  lockCount: number;
  /** When mode fell back to "mvcc" despite hybrid, why in-place wasn't possible. */
  inPlaceBlockedBy: string | null;
}

export interface PlanOptions {
  /** "occ": every txn optimistic. "hybrid": lock-infer where the footprint is static. */
  strategy?: "occ" | "hybrid";
  /** The checker's per-node types (the shared IR). The planner reads expression
   *  types from it — is a field scalar, is a receiver a collection — instead of
   *  re-deriving them. Required for "hybrid" (footprint analysis needs types); the
   *  interpreter and Go backend both have one to pass. */
  elaboration?: Elaboration;
}

// ---------------------------------------------------------------------------
// Planner
// ---------------------------------------------------------------------------

export function planLocks(program: ast.Program, opts: PlanOptions = {}): LockPlan {
  return new Planner(program, opts.strategy ?? "occ", opts.elaboration).plan();
}

/** One `txn`'s decided strategy paired with its source span. */
export interface TxnPlanEntry {
  span: ast.Span;
  strategy: TxnStrategy;
}

/**
 * The plan's per-`txn` decisions paired with their source spans, in source
 * order. `plan.strategies` is keyed by AST node identity (fine for a backend,
 * awkward for a UI); this flattens it into a located, ordered list for editors
 * and other source-oriented consumers.
 */
export function txnPlanEntries(plan: LockPlan): TxnPlanEntry[] {
  return [...plan.strategies.entries()]
    .map(([txn, strategy]) => ({ span: txn.span, strategy }))
    .sort((a, b) => a.span.start.offset - b.span.start.offset);
}

/** A JSON-friendly view of the plan (for `--emit-plan` and tests). */
export function lockPlanToJSON(plan: LockPlan): unknown {
  return {
    mode: plan.mode,
    txnCount: plan.txnCount,
    lockCount: plan.lockCount,
    inPlaceBlockedBy: plan.inPlaceBlockedBy,
    txns: [...plan.strategies.values()].map((s) =>
      s.kind === "lock" ? { strategy: "lock", footprint: s.footprint } : { strategy: "occ" }
    ),
  };
}

/** A field type gogen can lock in place: a scalar or an enum (never a nested
 *  object or collection). Read from the Elaboration, not re-classified here. */
function isLockableField(t: Type | undefined): boolean {
  return t !== undefined && (t.k === "int" || t.k === "float" || t.k === "bool" || t.k === "string" || t.k === "enum");
}

/** Whether an expression's type is a built-in collection (Set/Dict/Table). */
function isCollectionType(t: Type | undefined): boolean {
  return t?.k === "generic" && ast.isGenericContainer(t.name);
}

class Planner {
  private structs = new Map<string, ast.StructDecl>();
  private fns: ast.FnDecl[] = [];

  constructor(private program: ast.Program, private strategy: "occ" | "hybrid", private elab?: Elaboration) {
    for (const d of program.decls) {
      if (d.kind === "struct") this.structs.set(d.name, d);
      else if (d.kind === "fn") this.fns.push(d);
    }
    if (strategy === "hybrid" && !elab) {
      throw new Error("hybrid lock planning needs an Elaboration (pass opts.elaboration)");
    }
  }

  /** An expression's checker type, from the Elaboration. */
  private typeOf(e: ast.Expr): Type | undefined {
    return this.elab?.types.get(e);
  }

  plan(): LockPlan {
    const strategies = new Map<ast.TxnStmt, TxnStrategy>();
    let txnCount = 0;
    let lockCount = 0;

    for (const fn of this.fns) {
      // A conservative type environment: struct-typed parameters only. Locals/lets
      // are intentionally omitted — a txn whose footprint depends on one is treated
      // as non-lockable (OCC), which is safe (never wrong, only less optimized).
      const structVars = new Map<string, string>();
      for (const p of fn.params) {
        const sn = this.structTypeName(p.type);
        if (sn) structVars.set(p.name, sn);
      }
      this.forEachTxn(fn.body, (txn) => {
        txnCount++;
        const footprint =
          this.strategy === "hybrid" ? this.footprint(txn.body, structVars) : null;
        if (footprint) {
          lockCount++;
          strategies.set(txn, { kind: "lock", footprint });
        } else {
          strategies.set(txn, { kind: "occ" });
        }
      });
    }

    const { mode, blockedBy } = this.chooseMemoryMode(strategies, txnCount, lockCount);
    return { mode, strategies, txnCount, lockCount, inPlaceBlockedBy: blockedBy };
  }

  // -- per-txn footprint analysis ----------------------------------------

  /**
   * Returns the sorted list of root variables to lock if the txn's footprint is
   * statically enumerable, else null (→ OCC). Conservative: accepts scalar/enum
   * field reads and writes on struct-typed variables, arithmetic, and if/while
   * over those; rejects set iteration, calls, nested object access, match, go,
   * nested txn, and let (which could alias a nested object).
   */
  private footprint(body: ast.Block, structVars: Map<string, string>): string[] | null {
    const roots = new Set<string>();
    let ok = true;

    // A lockable root: `var.field` where `var` is a struct-typed *parameter* (the
    // conservative root set — see `structVars`) and the field's type (from the
    // Elaboration) is a scalar/enum, i.e. not a nested object or collection.
    const rootObject = (fa: ast.FieldAccess): void => {
      if (!ok) return;
      if (fa.object.kind !== "ident") { ok = false; return; } // a.b.c — middle is another object
      if (!structVars.has(fa.object.name) || !isLockableField(this.typeOf(fa))) { ok = false; return; }
      roots.add(fa.object.name);
    };

    const visitExpr = (e: ast.Expr): void => {
      if (!ok) return;
      switch (e.kind) {
        case "int": case "float": case "string": case "bool": case "ident":
          return;
        case "unary": visitExpr(e.operand); return;
        case "binary": visitExpr(e.left); visitExpr(e.right); return;
        case "field": rootObject(e); return;
        case "call": ok = false; return;
      }
    };

    const visitStmt = (s: ast.Stmt): void => {
      if (!ok) return;
      switch (s.kind) {
        case "assign":
          if (s.target.kind === "ident") {
            if (structVars.has(s.target.name)) { ok = false; return; } // object alias
            visitExpr(s.value);
          } else if (s.target.kind === "field") {
            rootObject(s.target);
            visitExpr(s.value);
          } else ok = false;
          return;
        case "exprStmt": visitExpr(s.expr); return;
        case "if":
          visitExpr(s.cond);
          s.then.stmts.forEach(visitStmt);
          if (s.else) {
            if (s.else.kind === "if") visitStmt(s.else);
            else s.else.stmts.forEach(visitStmt);
          }
          return;
        case "while": visitExpr(s.cond); s.body.stmts.forEach(visitStmt); return;
        case "block": s.stmts.forEach(visitStmt); return;
        case "retry": ok = false; return; // a lock txn runs once and never aborts, so it must never `retry`
        default: ok = false; // let, for, match, go, return, txn
      }
    };

    body.stmts.forEach(visitStmt);
    return ok ? [...roots].sort() : null;
  }

  // -- whole-program memory mode -----------------------------------------

  private chooseMemoryMode(
    strategies: Map<ast.TxnStmt, TxnStrategy>,
    txnCount: number,
    lockCount: number
  ): { mode: MemoryMode; blockedBy: string | null } {
    if (this.strategy !== "hybrid") return { mode: "mvcc", blockedBy: "strategy is occ" };
    if (txnCount === 0) return { mode: "mvcc", blockedBy: "no transactions" };
    if (lockCount !== txnCount) {
      return { mode: "mvcc", blockedBy: "some txn is optimistic (data-dependent footprint)" };
    }
    // In-place needs: no sets, and no object-field access outside a txn (that would
    // be an unlocked read/write racing the in-place mutation), and no print.
    const blocked = this.inPlaceBlocker();
    if (blocked) return { mode: "mvcc", blockedBy: blocked };
    return { mode: "in-place", blockedBy: null };
  }

  /** Returns a reason string if the program can't use in-place memory, else null. */
  private inPlaceBlocker(): string | null {
    for (const s of this.structs.values()) {
      for (const f of s.fields) {
        if (f.type.kind === "named" && ast.isGenericContainer(f.type.name))
          return `struct ${s.name} has a ${f.type.name} field`;
      }
    }
    let reason: string | null = null;
    for (const fn of this.fns) {
      const structVars = new Map<string, string>();
      for (const p of fn.params) {
        const sn = this.structTypeName(p.type);
        if (sn) structVars.set(p.name, sn);
      }
      this.walkOutsideTxn(fn.body, structVars, (r) => {
        if (!reason) reason = r;
      });
      if (reason) return reason;
    }
    return null;
  }

  /**
   * Walk statements that are NOT inside a txn, reporting anything that would make
   * in-place representation unsound: a heap-object field access (unlocked), a set
   * op, or a `print`. Field access *inside* txn bodies is fine (locked).
   */
  private walkOutsideTxn(
    body: ast.Block,
    structVars: Map<string, string>,
    report: (reason: string) => void
  ): void {
    const visitExpr = (e: ast.Expr): void => {
      switch (e.kind) {
        case "field":
          report("object field access outside a transaction");
          visitExpr(e.object);
          return;
        case "call":
          if (e.callee.kind === "ident" && e.callee.name === "print") {
            report("print (would read objects unlocked)");
          }
          if (e.callee.kind === "ident" && ast.isGenericContainer(e.callee.name)) report("uses a collection");
          // A method whose receiver's type (from the Elaboration) is a collection
          // — not guessed from the method's name.
          if (e.callee.kind === "field" && isCollectionType(this.typeOf(e.callee.object))) report("uses a collection");
          e.args.forEach((a) => visitExpr(a.value));
          if (e.callee.kind === "field") visitExpr(e.callee.object);
          return;
        case "list":
          // A collection literal builds a collection without naming one, so it
          // matches neither test above — without these cases a literal-only program
          // would be classified `in-place`, which the cell layout can't support.
          report("uses a collection");
          e.elements.forEach(visitExpr);
          return;
        case "brace":
          report("uses a collection");
          e.entries.forEach((en) => {
            if (en.key) visitExpr(en.key);
            visitExpr(en.value);
          });
          return;
        case "binary": visitExpr(e.left); visitExpr(e.right); return;
        case "unary": visitExpr(e.operand); return;
        default: return;
      }
    };
    const visitStmt = (s: ast.Stmt): void => {
      switch (s.kind) {
        case "txn": return; // field access inside a locked txn is fine
        case "let": visitExpr(s.value); return;
        case "assign":
          if (s.target.kind === "field") report("object field assignment outside a transaction");
          visitExpr(s.value);
          return;
        case "exprStmt": visitExpr(s.expr); return;
        case "return": if (s.value) visitExpr(s.value); return;
        case "if":
          visitExpr(s.cond);
          s.then.stmts.forEach(visitStmt);
          if (s.else) { if (s.else.kind === "if") visitStmt(s.else); else s.else.stmts.forEach(visitStmt); }
          return;
        case "while": visitExpr(s.cond); s.body.stmts.forEach(visitStmt); return;
        case "for": visitExpr(s.iterable); s.body.stmts.forEach(visitStmt); return;
        case "match":
          visitExpr(s.subject); s.arms.forEach((a) => a.body.stmts.forEach(visitStmt)); return;
        case "go": s.call.args.forEach((a) => visitExpr(a.value)); return;
        case "raise": visitExpr(s.value); if (s.cause) visitExpr(s.cause); return;
        case "try":
          s.body.stmts.forEach(visitStmt);
          s.handler.body.stmts.forEach(visitStmt);
          return;
        case "block": s.stmts.forEach(visitStmt); return;
      }
    };
    body.stmts.forEach(visitStmt);
  }

  // -- helpers ------------------------------------------------------------

  private structTypeName(t: ast.TypeExpr): string | null {
    return t.kind === "named" && this.structs.has(t.name) ? t.name : null;
  }

  private forEachTxn(body: ast.Block, fn: (txn: ast.TxnStmt) => void): void {
    const visit = (s: ast.Stmt): void => {
      switch (s.kind) {
        case "txn":
          fn(s);
          s.body.stmts.forEach(visit); // (nested txns exist in the AST though we reject them)
          return;
        case "if":
          s.then.stmts.forEach(visit);
          if (s.else) { if (s.else.kind === "if") visit(s.else); else s.else.stmts.forEach(visit); }
          return;
        case "while": case "for": s.body.stmts.forEach(visit); return;
        case "match": s.arms.forEach((a) => a.body.stmts.forEach(visit)); return;
        case "try":
          s.body.stmts.forEach(visit);
          s.handler.body.stmts.forEach(visit);
          return;
        case "block": s.stmts.forEach(visit); return;
        default: return;
      }
    };
    body.stmts.forEach(visit);
  }
}
